import React, { useEffect, lazy, Suspense } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LandingPage } from './pages/LandingPage';
import { HomePage } from './pages/HomePage';
import { DashboardPage } from './pages/DashboardPage';
import { AuthPage } from './pages/AuthPage';

// Lazy load non-critical routes for better code splitting
const NotesPage = lazy(() => import('./pages/NotesPage').then(m => ({ default: m.NotesPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ClientsPage = lazy(() => import('./pages/ClientsPage').then(m => ({ default: m.ClientsPage })));
const OfficesPage = lazy(() => import('./pages/OfficesPage').then(m => ({ default: m.OfficesPage })));
const OfficeDetailsPage = lazy(() => import('./pages/OfficeDetailsPage').then(m => ({ default: m.OfficeDetailsPage })));
const ClientDetailsPage = lazy(() => import('./pages/ClientDetailsPage').then(m => ({ default: m.ClientDetailsPage })));
const TransferRequestsPage = lazy(() => import('./pages/TransferRequestsPage').then(m => ({ default: m.TransferRequestsPage })));
const HelpPage = lazy(() => import('./pages/HelpPage').then(m => ({ default: m.HelpPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TherapyProgressNotesPage = lazy(() => import('./pages/TherapyProgressNotesPage').then(m => ({ default: m.TherapyProgressNotesPage })));
const PaywallPage = lazy(() => import('./pages/PaywallPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const TeamStatsPage = lazy(() => import('./pages/TeamStatsPage'));
const OrganizationPage = lazy(() => import('./pages/OrganizationPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const NinetyDayEvalPage = lazy(() => import('./pages/NinetyDayEvalPage'));
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );
}

function RedirectToDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === undefined) return; // Wait until Supabase finishes checking

    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const resetFlow = searchParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery';

    const currentPath = window.location.pathname;

    // ✅ Allow users to stay on landing page ("/") even if not logged in
    if (!user && currentPath === '/') return;

    // ✅ Do NOT redirect if user is in password reset mode
    if (!user && resetFlow) return;

    // ✅ Redirect logged-out users trying to access protected routes
    const protectedRoutes = ['/app', '/clients', '/offices', '/profile', '/settings', '/help'];
    if (!user && protectedRoutes.some(route => currentPath.startsWith(route))) {
      navigate('/auth', { replace: true });
    }

    // ✅ Redirect logged-in users to "/app" if they are on "/auth" or "/"
    if (user && (currentPath === '/' || currentPath === '/auth')) {
      navigate('/app', { replace: true });
    }

  }, [user, navigate]);

  return null;
}



function ProtectedRoute({ children, requiresAccess = false }: { children: React.ReactNode; requiresAccess?: boolean }) {
  const { user, loading, accessResult, checkingAccess, refreshAccessCheck } = useAuth();

  // Show loading spinner ONLY while authenticating (not while checking access)
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" />;
  }

  // Show error modal if access check failed (after all retries)
  if (requiresAccess && accessResult?.error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Verify Access</h2>
          <p className="text-gray-600 mb-6">
            We're having trouble verifying your subscription status. Please check your connection and try again.
          </p>
          {accessResult.errorMessage && (
            <p className="text-sm text-gray-500 mb-6 font-mono bg-gray-100 p-3 rounded">
              {accessResult.errorMessage}
            </p>
          )}
          <button
            onClick={refreshAccessCheck}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Redirect to paywall if access check completed and user has no access
  if (requiresAccess && accessResult && !accessResult.hasAccess) {
    return <Navigate to="/app/paywall" replace />;
  }

  // Render children immediately - show progressive loading banner if access check is still pending
  return (
    <>
      {requiresAccess && checkingAccess && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-blue-50 border-b border-blue-200 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
            <span className="text-sm text-blue-900 font-medium">Verifying access...</span>
          </div>
        </div>
      )}
      {children}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={
            <>
              <RedirectToDashboard />
              <LandingPage />
            </>
          } />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/terms" element={
            <Suspense fallback={<PageLoader />}>
              <TermsPage />
            </Suspense>
          } />
          <Route path="/privacy" element={
            <Suspense fallback={<PageLoader />}>
              <PrivacyPage />
            </Suspense>
          } />
          <Route path="/therapy-progress-notes" element={
            <Suspense fallback={<PageLoader />}>
              <TherapyProgressNotesPage />
            </Suspense>
          } />
          <Route
            path="/organization/:organizationId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <OrganizationPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invite/:token"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <InvitePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          >
            <Route index element={
              <ProtectedRoute requiresAccess={true}>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="notes" element={
              <ProtectedRoute requiresAccess={true}>
                <Suspense fallback={<PageLoader />}>
                  <NotesPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="90-day-eval" element={
              <ProtectedRoute requiresAccess={true}>
                <Suspense fallback={<PageLoader />}>
                  <NinetyDayEvalPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="clients" element={
              <ProtectedRoute requiresAccess={true}>
                <Suspense fallback={<PageLoader />}>
                  <ClientsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="offices" element={
              <ProtectedRoute requiresAccess={true}>
                <Suspense fallback={<PageLoader />}>
                  <OfficesPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="offices/:id" element={
              <ProtectedRoute requiresAccess={true}>
                <Suspense fallback={<PageLoader />}>
                  <OfficeDetailsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="clients/:id" element={
              <ProtectedRoute requiresAccess={true}>
                <Suspense fallback={<PageLoader />}>
                  <ClientDetailsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="transfers" element={
              <ProtectedRoute requiresAccess={true}>
                <Suspense fallback={<PageLoader />}>
                  <TransferRequestsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="profile" element={
              <Suspense fallback={<PageLoader />}>
                <ProfilePage />
              </Suspense>
            } />
            <Route path="settings" element={
              <Suspense fallback={<PageLoader />}>
                <SettingsPage />
              </Suspense>
            } />
            <Route path="help" element={
              <Suspense fallback={<PageLoader />}>
                <HelpPage />
              </Suspense>
            } />
            <Route path="team-stats" element={
              <Suspense fallback={<PageLoader />}>
                <TeamStatsPage />
              </Suspense>
            } />
            <Route path="billing" element={
              <Suspense fallback={<PageLoader />}>
                <BillingPage />
              </Suspense>
            } />
            <Route path="paywall" element={
              <Suspense fallback={<PageLoader />}>
                <PaywallPage />
              </Suspense>
            } />
            <Route path="admin" element={
              <Suspense fallback={<PageLoader />}>
                <AdminPage />
              </Suspense>
            } />
          </Route>
        </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
